import * as React from 'react';
import {withTheme} from 'emotion-theming';
import {Theme} from '../../theme';
import {px} from '../../utils';
import Flex, {FlexProps} from '../Flex';
import Step from './Step';
import StepRenderer from './private/StepRenderer';
import {StepProps, StepRendererProps, StepsProps, StepStatus, StepPosition} from './types';

const defaultGetStepNumber = (index: number) => index + 1;

// TODO: add docs, tests, step render function
class Steps extends React.Component<StepsProps & {theme?: Theme}, {}> {
  public static defaultProps = {
    getStepNumber: defaultGetStepNumber
  };

  public render() {
    const {
      children,
      vertical,
      center,
      dot,
      getStepNumber,
      stepTitle,
      stepHeads,
      customClassName,
      customStyle,
      theme,
      ...restProps
    } = this.props;

    const steps = React.Children.map(children, (child, index) => {
      if (React.isValidElement(child) && child.type === Step) {
        const rendererProps = {
          style: this._getStepStyle(index),
          status: this._getStepStatus(index),
          stepPosition: this._getStepPosition(index),
          stepNumber: getStepNumber!(index),
          stepIndex: index,
          isCurrentStep: this._isCurrentStep(index),
          vertical: Boolean(vertical),
          center: Boolean(center && !vertical),
          dot: Boolean(dot),
          renderTitle: stepTitle,
          stepHeads,
          customClassName,
          customStyle,
          ...(child.props as StepProps)
        } as StepRendererProps;

        return <StepRenderer {...rendererProps} />;
      }
      return null;
    });

    const flexProps = {
      ...(vertical
        ? {
            direction: 'column',
            style: {
              height: '100%',
              padding: `${px(theme!.steps.vertical.paddingY)} 0`
            }
          }
        : {}),
      ...restProps
    };

    return <Flex {...flexProps as FlexProps}>{steps}</Flex>;
  }

  private get _numberOfSteps() {
    return React.Children.count(this.props.children);
  }

  private _isCurrentStep = (index: number) => this.props.currentStepIndex === index;

  private _isFirstStep = (index: number) => 0 === index;

  private _isLastStep = (index: number) => this._numberOfSteps - 1 === index;

  private _getStepPosition = (index: number): StepPosition => {
    if (this._isFirstStep(index)) {
      return StepPosition.FIRST;
    }
    if (this._isLastStep(index)) {
      return StepPosition.LAST;
    }
    return StepPosition.INBETWEEN;
  };

  private _getStepStyle = (index: number): React.CSSProperties => {
    const {center, vertical} = this.props;

    if (this._isLastStep(index) && !center) {
      return {flex: '0 0 auto', maxWidth: !vertical ? `${100 / this._numberOfSteps}%` : undefined};
    }
    return {flexBasis: `${100 / (this._numberOfSteps - 1)}%`};
  };

  private _getStepStatus = (index: number): StepStatus => {
    if (index < this.props.currentStepIndex) {
      return 'finished';
    }
    if (index === this.props.currentStepIndex) {
      return this.props.status || 'processing';
    }
    return 'pending';
  };
}

export default withTheme(Steps);



// WEBPACK FOOTER //
// ./src/components/Steps/Steps.tsx